import React, { useEffect, useState } from "react";
import moderatorBlogStyles from "src/Assets/css/Moderator/ModeratorBlog.module.css";
import { Menu, MenuItem } from "@szhsin/react-menu";
import { PiDotsThreeBold } from "react-icons/pi";
import IconButton from "@material-ui/core/IconButton";
import Add from "src/Assets/images/Add.svg";
import {
  capitalizeFirstLetter,
  ignoreSpacesAndUrlsFromMsg,
} from "src/Utils/commonUtils";
import API from "../../../network";
import ModeratorBlogReusableAlertPopup from "./ModeratorBlogReusableAlertPopup";
import TextareaAutosize from 'react-textarea-autosize';
import editIcon from "src/Assets/images/edit.svg";
import deleteIcon from "src/Assets/images/Delete.svg";

const ModeratorBlog = (props) => {
  const { tenantId } = props;

  const [charCount, setCharCount] = useState(0);
  const [blogText, setBlogText] = useState("");
  const [isPostingBlog, setIsPostingBlog] = useState(false);

  // Indicates whether a blog already exists for the selected community
  const [hasExistingBlog, setHasExistingBlog] = useState(false);
  const [isEditingBlog, setIsEditingBlog] = useState(false);
  // Track creation of a new blog while an existing blog is present
  const [isCreatingNewBlog, setIsCreatingNewBlog] = useState(false);

  // Preserve original community blog content for cancel operations
  const [originalBlogText, setOriginalBlogText] = useState("");

  // Control New Blog confirmation popup
  const [showNewBlogPopup, setShowNewBlogPopup] = useState(false);
  const [showDeleteBlogPopup, setShowDeleteBlogPopup] = useState(false);

  useEffect(() => {
    setBlogText("");
    setCharCount(0);
  }, [tenantId]);

  // Load community blog whenever the selected tenant changes
  useEffect(() => {
    const getCommunityBlog = async () => {
      try {
        setOriginalBlogText("");
        setBlogText("");
        setCharCount(0);
        setHasExistingBlog(false);

        const response = await API.getCommunityBlog(tenantId);

        const blogContent = response?.data?.moderatorBlog || "";

        if (blogContent) {
          // Store original blog content
          setOriginalBlogText(blogContent);

          setBlogText(blogContent);
          setCharCount(ignoreSpacesAndUrlsFromMsg(blogContent).msgLength);
          setHasExistingBlog(true);
          // Existing blogs are initially shown in view mode
          setIsEditingBlog(false);
        }
      } catch (error) {
        console.error("Error fetching community blog:", error);
      }
    };

    if (tenantId) {
      getCommunityBlog();
    }
  }, [tenantId]);

  const onBlogTextChange = (e) => {
    const textValue = e.target.value;
    const formattedValue = capitalizeFirstLetter(textValue);
    const { msgLength } = ignoreSpacesAndUrlsFromMsg(formattedValue);
    setCharCount(msgLength);
    setBlogText(formattedValue);
  };

  // User confirmed creation of a new blog
  const onContinueNewBlog = () => {
    setShowNewBlogPopup(false);

    // Switch to new blog creation mode
    setIsCreatingNewBlog(true);

    // Clear editor for new blog content
    setBlogText("");
    setCharCount(0);
  };
  // Cancel blog creation or editing
  const onCancel = () => {
    // Cancel New Blog flow
    if (isCreatingNewBlog) {
      setBlogText(originalBlogText);
      setCharCount(ignoreSpacesAndUrlsFromMsg(originalBlogText).msgLength);

      setIsCreatingNewBlog(false);
      return;
    }

    // Cancel Edit flow
    if (isEditingBlog) {
      setBlogText(originalBlogText);
      setCharCount(ignoreSpacesAndUrlsFromMsg(originalBlogText).msgLength);

      setIsEditingBlog(false);
      return;
    }

    // Cancel initial blog creation
    setBlogText("");
    setCharCount(0);
  };

  // Close New Blog confirmation popup
  const onCancelNewBlogPopup = () => {
    setShowNewBlogPopup(false);
  };

  // Enable editing for an existing blog
  const onEditBlog = () => {
    setIsEditingBlog(true);
  };

  // Open delete confirmation popup
  const onDeleteBlog = () => {
    setShowDeleteBlogPopup(true);
  };

  // Close delete confirmation popup
  const onCancelDeleteBlog = () => {
    setShowDeleteBlogPopup(false);
  };

  // Delete current community blog
  const onConfirmDeleteBlog = async () => {
    try {
      const formData = new FormData();

      formData.append("tenantid", tenantId);
      formData.append("task", "delete");

      const response = await API.deleteCurrentBlog(formData);

      const statusMessage = response?.data?.Status;

      if (!statusMessage) return;

      alert(statusMessage);

      // Close popup
      setShowDeleteBlogPopup(false);

      // Reset blog state after successful deletion
      if (
        statusMessage.toLowerCase().includes("delete") ||
        statusMessage.toLowerCase().includes("deleted")
      ) {
        setBlogText("");
        setOriginalBlogText("");
        setCharCount(0);

        setHasExistingBlog(false);
        setIsEditingBlog(false);
        setIsCreatingNewBlog(false);
      }
    } catch (error) {
      console.error("Error while deleting blog:", error);
    }
  };

  // Start creating a new blog and archive the current one
  const onCreateNewBlog = () => {
    setShowNewBlogPopup(true);

    // Will be replaced later:
    // Show confirmation popup
    // Archive existing blog
    // Clear textarea after user confirmation

    // Temporary behavior matches Edit
  };

  // Create or update community blog
  const postBlog = async () => {
    try {
      if (!blogText.trim() || charCount > 280) return;

      setIsPostingBlog(true);

      let response;

      // Update existing blog
      if (isEditingBlog) {
        const formData = new FormData();
        formData.append("tenantid", tenantId);
        formData.append("task", "edit");
        formData.append("editedText", blogText.trim());

        response = await API.editAndRePostCurrentBlog(formData);
      } else {
        // Create first blog or create a new blog
        const formData = new FormData();
        formData.append("blogText", blogText.trim());
        formData.append("tenantid", tenantId);

        response = await API.postBlog(formData);
      }

      const statusMessage =
        response?.data?.Status ||
        response?.data?.status ||
        response?.data?.message;

      if (statusMessage) {
        alert(statusMessage);
      }

      const normalizedStatus = (statusMessage || "").toLowerCase().trim();
      const isError =
        normalizedStatus.includes("fail") ||
        normalizedStatus.includes("error");

      // Successful create/edit
      if (
        !isError &&
        (normalizedStatus.includes("posted") ||
          normalizedStatus.includes("edited") ||
          normalizedStatus.includes("updated") ||
          normalizedStatus.includes("created") ||
          normalizedStatus.includes("success") ||
          (!statusMessage && response?.status === 200))
      ) {
        // Save latest blog as source of truth
        setOriginalBlogText(blogText);

        setHasExistingBlog(true);
        setIsEditingBlog(false);
        setIsCreatingNewBlog(false);
      }
    } catch (error) {
      console.error("Error while posting blog:", error);
    } finally {
      setIsPostingBlog(false);
    }
  };

  return (
    <div className={moderatorBlogStyles.blogPostContainer}>
      {/* Create a new blog while archiving the existing one */}
      {hasExistingBlog && (
        <div className={moderatorBlogStyles.newBlogContainer}>
          <button
            type="button"
            className={moderatorBlogStyles.newBlogButton}
            onClick={onCreateNewBlog}
            disabled={isCreatingNewBlog}
          >
            <span className={moderatorBlogStyles.newBlogAddBtnText}>
              <img src={Add} alt="" />
              New Blog
            </span>
          </button>
        </div>
      )}
      <div className={moderatorBlogStyles.textAreaContainer}>
        {hasExistingBlog && !isCreatingNewBlog && (
          <div className={moderatorBlogStyles.inlineActionContainer}>
            <Menu viewScroll="close" reposition="auto" position="anchor"
              menuButton={
                <IconButton
                  disabled={isEditingBlog}
                  style={{
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    outline: "none",
                    opacity: isEditingBlog ? 0.5 : 1,
                  }}
                >
                  <PiDotsThreeBold fontSize={22} color="rgba(0, 0, 0, 0.7)" />
                </IconButton>
              }
              transition
              direction="bottom"
              arrow
              align="end"
              menuStyle={{
                backgroundColor: "white",
                borderRadius: 20,
                minWidth: "max-content",
              }}
            >
              <MenuItem onClick={onEditBlog}>
                <div style={{ display: "flex", alignItems: "center", width: "100%", height: "100%", padding: "0.375rem 1.5rem", boxSizing: "border-box", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <img src={editIcon} style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0, objectFit: "contain" }} alt="" />
                  <span style={{ fontSize: 14, whiteSpace: "nowrap" }}>Edit</span>
                </div>
              </MenuItem>

              <MenuItem onClick={onDeleteBlog}>
                <div style={{ display: "flex", alignItems: "center", width: "100%", height: "100%", padding: "0.375rem 1.5rem", boxSizing: "border-box", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <img src={deleteIcon} style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0, objectFit: "contain" }} alt="" />
                  <span style={{ fontSize: 14, whiteSpace: "nowrap" }}>Delete</span>
                </div>
              </MenuItem>
            </Menu>
          </div>
        )}

        <TextareaAutosize
          name="blogPost"
          id="blogPost"
          className={moderatorBlogStyles.blogPostTextArea}
          placeholder="What would you like to blog?"
          value={blogText}
          onChange={onBlogTextChange}
          disabled={hasExistingBlog && !isEditingBlog && !isCreatingNewBlog}
          
          /* Force the autosizer to render exactly 12 to 14 rows */
          minRows={12} 
          maxRows={14} 
        />

        <div className={moderatorBlogStyles.charCountContainer}>
          <label style={{ color: charCount > 280 ? "red" : "#666" }}>
            {charCount}
          </label>
          <label>/280</label>
        </div>
      </div>

      {(!hasExistingBlog || isEditingBlog || isCreatingNewBlog) && (
        <div className={moderatorBlogStyles.buttonContainer}>
          <button
            className={moderatorBlogStyles.postButton}
            disabled={!blogText.trim() || charCount > 280 || isPostingBlog}
            onClick={postBlog}
          >
            {isPostingBlog ? "Posting..." : "Post"}
          </button>
          <button
            type="button"
            className={moderatorBlogStyles.cancelButton}
            onClick={onCancel}
            disabled={
              isPostingBlog ||
              (!isCreatingNewBlog && !isEditingBlog && !blogText.trim())
            }
          >
            Cancel
          </button>
        </div>
      )}

      <ModeratorBlogReusableAlertPopup
        open={showNewBlogPopup}
        title="New Blog"
        message="Creating a new blog will archive the current blog"
        onClose={onCancelNewBlogPopup}
        onContinue={onContinueNewBlog}
      />
      <ModeratorBlogReusableAlertPopup
        open={showDeleteBlogPopup}
        title="Delete Blog"
        message="Are you sure you want to delete the current blog?"
        onClose={onCancelDeleteBlog}
        onContinue={onConfirmDeleteBlog}
      />
    </div>
  );
};

export default ModeratorBlog;
